import { unwrapResult } from "@reduxjs/toolkit";
import { startOfMonth } from "date-fns";
import { FormikProvider, useFormik } from "formik";
import PrimaryButton from "library/Atoms/Button/PrimaryButton";
import ModalLayout from "library/Layouts/ModalLayout";
import DateRangePicker from "library/Molecules/DatePicker/DateRangePicker";
import MonthPicker from "library/Molecules/DatePicker/MonthPicker";
import PropTypes from "prop-types";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { downloadOrdersReportRequest } from "store/requests/order.requests";
import { endDayOfMonth, noonOfDay } from "utility/date-fns";
import OrdersReportFormValidation from "./OrdersReportForm.validation";

const OrdersReportForm = ({ onClose }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const reportForm = useFormik({
    initialValues: {
      month: new Date(),
      start: noonOfDay(startOfMonth(new Date())),
      end: noonOfDay(endDayOfMonth(new Date())),
    },
    onSubmit: ({ start, end }, { setSubmitting }) => {
      dispatch(downloadOrdersReportRequest({ start, end }))
        .then(unwrapResult)
        .then(() => {
          setSubmitting(false);
          onClose();
        })
        .catch(() => setSubmitting(false));
    },
    validationSchema: OrdersReportFormValidation,
    validateOnBlur: true,
  });
  const {
    values: { month, start, end },
    setFieldValue,
    submitForm,
    isSubmitting,
    isValid,
  } = reportForm;

  const onChangeMonth = useCallback(
    (newMonth) => {
      if (!newMonth) {
        setFieldValue("month", null);
        setFieldValue("start", null);
        setFieldValue("end", null);
        return;
      }

      setFieldValue("month", newMonth);
      setFieldValue("start", noonOfDay(startOfMonth(newMonth)));
      setFieldValue("end", noonOfDay(endDayOfMonth(newMonth)));
    },
    [setFieldValue]
  );

  const onChangeDateRange = useCallback(
    (_, value) => {
      const [rangeStart, rangeEnd] = value || [];

      setFieldValue("start", rangeStart ? new Date(rangeStart).toISOString() : null);
      setFieldValue("end", rangeEnd ? new Date(rangeEnd).toISOString() : null);
    },
    [setFieldValue]
  );

  return (
    <FormikProvider value={reportForm}>
      <ModalLayout
        className="d-flex flex-column gap-5 pb-2"
        modalControls={
          <PrimaryButton
            id="submit-report-modal-btn"
            className="mt-4 w-100"
            label={t("common.buttons.confirmBtn")}
            onClick={submitForm}
            loading={isSubmitting}
            disabled={!isValid}
          />
        }
      >
        <MonthPicker
          id="report-month"
          name="month"
          value={month}
          label={t("dashboard.forms.downloadReportForm.monthLabel")}
          placeholder={t("dashboard.forms.downloadReportForm.monthPlaceholder")}
          onChange={onChangeMonth}
          required
        />

        <DateRangePicker
          id="report-interval"
          name="interval"
          label={t("dashboard.forms.downloadReportForm.dateRangeLabel")}
          value={[start, end]}
          placeholder={t("dashboard.forms.downloadReportForm.dateRangePlaceholder")}
          onSelect={onChangeDateRange}
          minDate={month ? startOfMonth(month) : null}
          disabled={!month}
          required
        />
      </ModalLayout>
    </FormikProvider>
  );
};

OrdersReportForm.propTypes = {
  onClose: PropTypes.func.isRequired,
};

export default OrdersReportForm;
